// ClientDashboard.jsx - vista consolidada del alumno

import React, { useEffect, useState } from "react";
import {
  Card, CardBody, CardHeader, CardTitle,
  Row, Col, Table, Button, Input, FormGroup, Label
} from "reactstrap";
import { getBookingsByClient, getClientDashboard, getClientClasesPorMes } from "../service/clientsService";
import { applyPenalty } from "../service/paymentsService";
import api from "../../../../api/api";
import toast from "react-hot-toast";
import ReactLoading from "react-loading";
import { overlayStyles, loaderContainerStyles } from "styles/commonStyles";

const monthNames = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

export default function ClientDashboard({ clientId }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [controlData, setControlData] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingControl, setLoadingControl] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const isLoading = loadingDashboard || loadingControl || loadingBookings;

  useEffect(() => {
    if (!clientId) return;
    const fetchAll = async () => {
      setLoadingDashboard(true);
      setLoadingBookings(true);
      try {
        const [dashboardData, bookingData] = await Promise.all([
          getClientDashboard(clientId),
          getBookingsByClient(clientId)
        ]);
        setDashboardData(dashboardData);
        setBookings(bookingData);
      } catch (err) {
        console.error("Error loading dashboard:", err);
        toast.error("Error al cargar información del alumno");
      } finally {
        setLoadingDashboard(false);
        setLoadingBookings(false);
      }
    };
    fetchAll();
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const fetchControl = async () => {
      setLoadingControl(true);
      try {
        const data = await getClientClasesPorMes(clientId, year, month);
        setControlData(data);
      } catch (err) {
        console.error("Error loading control data:", err);
        toast.error("Error al cargar control mensual");
      } finally {
        setLoadingControl(false);
      }
    };
    fetchControl();
  }, [clientId, year, month]);

  useEffect(() => {
    const filtered = bookings.filter(b => {
      const date = new Date(b.class_date);
      return date.getFullYear() === year && (date.getMonth() + 1) === month;
    });
    setFilteredBookings(filtered);
  }, [bookings, year, month]);

  const handlePenalty = async (amount) => {
    if (!window.confirm(`¿Asignar penalización de Q${amount}?`)) return;
    try {
      await applyPenalty({ client_id: clientId, amount });
      toast.success("Penalización aplicada");
    } catch {
      toast.error("Error al aplicar penalización");
    }
  };

  const calcularObservacion = () => {
    if (!controlData || !dashboardData?.client) return '';
    if (!dashboardData.current_membership?.is_active) return '';
    const totalClases = (controlData.valid_classes || 0) + (controlData.no_show_classes || 0);
    if (totalClases <= 1 && new Date(dashboardData.client.created_at).getMonth() === (month - 1)) return 'NUEVA';
    if (totalClases >= controlData.expected_classes) return 'RENOVACIÓN TEMPRANA';
    return 'RENOVACIÓN';
  };

  const fechaSiguienteRenovacion = () => {
    const fecha = new Date(year, month - 1, 1);
    fecha.setMonth(fecha.getMonth() + 1);
    return fecha.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }).replace('.', '');
  };

  if (!dashboardData?.client) return null;

  return (
    <>
      {isLoading && (
        <div style={overlayStyles}>
          <div style={loaderContainerStyles}>
            <ReactLoading type="spin" color="white" height="40%" />
          </div>
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle tag="h4">Información del Alumno</CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <p><strong>Nombre:</strong> {dashboardData.client.first_name} {dashboardData.client.last_name}</p>
              <p><strong>Correo:</strong> {dashboardData.client.email}</p>
              <p><strong>Teléfono:</strong> {dashboardData.client.phone}</p>
              <p><strong>DPI:</strong> {dashboardData.client.dpi || '—'}</p>
            </Col>
            <Col md={6}>
              <p><strong>Membresía activa:</strong> {dashboardData.current_membership.name}</p>
              <p><strong>Clases por mes:</strong> {dashboardData.current_membership.classes_per_month}</p>
              <p><strong>Válida hasta:</strong> {dashboardData.current_membership.valid_until || '—'}</p>
              <p><strong>Estado:</strong> 
                <span className={`badge badge-${dashboardData.client.status_color} ml-2`}>
                  {dashboardData.client.status}
                </span>
              </p>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Estadísticas del Dashboard */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle tag="h4">Estadísticas Generales</CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={3}>
              <div className="text-center">
                <h4 className="text-primary">{dashboardData.statistics.total_bookings}</h4>
                <p className="text-muted mb-0">Total Reservas</p>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4 className="text-success">{dashboardData.statistics.attended_bookings}</h4>
                <p className="text-muted mb-0">Asistidas</p>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4 className="text-danger">{dashboardData.statistics.no_show_bookings}</h4>
                <p className="text-muted mb-0">Inasistencias</p>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4 className="text-info">{dashboardData.statistics.attendance_rate}%</h4>
                <p className="text-muted mb-0">Tasa de Asistencia</p>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle tag="h4">Control de Clases</CardTitle>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <FormGroup>
                <Label>Mes</Label>
                <Input type="select" value={month} onChange={e => setMonth(Number(e.target.value))}>
                  {monthNames.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Año</Label>
                <Input type="number" value={year} onChange={e => setYear(Number(e.target.value))} />
              </FormGroup>
            </Col>
          </Row>

          {controlData ? (
            <div>
              <Table responsive bordered>
                <thead>
                  <tr>
                    <th># Clases Paquete</th>
                    <th>Tomadas</th>
                    <th>Inasistencias</th>
                    <th>Restantes</th>
                    <th>Penalización</th>
                    <th>Renovación</th>
                    <th>Observación</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{controlData.expected_classes}</td>
                    <td>{controlData.valid_classes}</td>
                    <td>{controlData.no_show_classes}</td>
                    <td>{controlData.expected_classes - controlData.valid_classes - controlData.no_show_classes}</td>
                    <td>Q{parseFloat(controlData.penalty || 0).toFixed(2)}</td>
                    <td>{fechaSiguienteRenovacion()}</td>
                    <td>{calcularObservacion()}</td>
                    <td>
                      {controlData.penalty > 0 ? (
                        <Button color="danger" size="sm" onClick={() => handlePenalty(controlData.penalty)}>
                          Aplicar Penalización
                        </Button>
                      ) : '-'}
                    </td>
                  </tr>
                </tbody>
              </Table>
              
              {/* Debug info - temporal */}
              {controlData.debug_info && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6>Debug Info (temporal):</h6>
                  <p><strong>Cliente creado:</strong> {controlData.debug_info.client_created_date}</p>
                  <p><strong>Período consultado:</strong> {controlData.debug_info.period_start} a {controlData.debug_info.period_end}</p>
                  <p><strong>Pago realizado:</strong> {controlData.debug_info.payment_date}</p>
                  <p><strong>Pago válido hasta:</strong> {controlData.debug_info.payment_valid_until}</p>
                </div>
              )}
            </div>
          ) : <p>No hay datos de control este mes.</p>}
        </CardBody>
      </Card>

      <Row>
        <Col md={8}>
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Historial de Reservas</CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive bordered>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Horario</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((item, index) => (
                    <tr key={index}>
                      <td>{item.class_date}</td>
                      <td>{item.schedule || '—'}</td>
                      <td>{
                        item.attendance_status === 'attended' ? 'Asistió' :
                        item.attendance_status === 'no_show' ? 'Inasistencia' :
                        item.attendance_status === 'cancelled' ? 'Cancelada' : 'Pendiente'
                      }</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Historial de Pagos</CardTitle>
            </CardHeader>
            <CardBody>
              {dashboardData.payment_history && dashboardData.payment_history.length > 0 ? (
                <div>
                  {dashboardData.payment_history.map((payment, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                      <div className="d-flex justify-content-between">
                        <strong>Q{payment.amount}</strong>
                        <span className={`badge badge-${payment.is_active ? 'success' : 'secondary'}`}>
                          {payment.is_active ? 'Activo' : 'Vencido'}
                        </span>
                      </div>
                      <small className="text-muted d-block">{payment.membership_name}</small>
                      <small className="text-muted">
                        Pagado: {payment.date_paid} | Válido hasta: {payment.valid_until}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No hay historial de pagos</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
}